import { Injectable, inject } from "@angular/core";
import { AssessmentQuestion } from "../models/assessment/assessment-question.model";
import {
  collection,
  doc,
  documentId,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "@angular/fire/firestore";
import { AssessmentQuestionTag } from "../models/assessment/assessment-question-tag.model";
import { Lesson } from "../models/lesson.model";
import { Assessment, QuestionTypeFilter } from "../models/assessment/assessment.model";
import { createFirestoreId } from "../common/firestore-utils";
import { LoadingService } from "../shared/services/loading.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { firstValueFrom } from "rxjs";
import { MessagesService } from "../shared/services/messages.service";

@Injectable({
  providedIn: "root",
})
export class QuestionBankService {
  private firestore = inject(Firestore);
  private loading = inject(LoadingService);
  private httpClient = inject(HttpClient);
  private messages = inject(MessagesService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  async getAllQuestions(tenantId: string): Promise<AssessmentQuestion[]> {
    try {

      const filteredSnapshot = await getDocs(
        query(
          collection(this.firestore, `schools/${tenantId}/assessmentQuestions`),
          where('isDeleted', '==', false),
          orderBy('createdAt', 'desc')
        )
      );
  
      if (filteredSnapshot.empty) {
        return [];
      }

      return filteredSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AssessmentQuestion[];
    } catch (error) {
      console.error("Error fetching assessment questions:", error);
      throw new Error(
        "Failed to retrieve assessment questions. Please try again later."
      );
    }
  }

  async getQuestionsByIds(tenantId: string, questionIds: string[]): Promise<AssessmentQuestion[] | undefined> {
      try {
        const questionDocs = await getDocs(query(collection(this.firestore, `schools/${tenantId}/assessmentQuestions`), 
        where(documentId(), "in", questionIds),
        where('isDeleted', '==', false)));
        return questionDocs.docs.map((questionDoc) => ({ id: questionDoc.id, ...questionDoc.data() } as AssessmentQuestion));
      } catch (error) {
        console.error("Error fetching questions:", error);
        throw new Error("Failed to retrieve questions by IDs. Please try again later.");
      }
    }

  async insertQuestion(
    question: AssessmentQuestion,
    tenantId: string
  ): Promise<string> {
    try {
      const id = this.generateId();
      question.id = id;

      const docRef = doc(
        this.firestore,
        `schools/${tenantId}/assessmentQuestions`,
        id
      );

      await setDoc(docRef, question);
      return docRef.id;
    } catch (error) {
      console.error("Error inserting question:", error);
      throw new Error("Failed to insert question. Please try again later.");
    }
  }




  async deleteQuestion(tenantId: string, question: AssessmentQuestion) {
    try {
      const questionRef = doc(
        this.firestore,
        `schools/${tenantId}/assessmentQuestions/${question.id}`
      );
      await updateDoc(questionRef, {
        deletedAt: Timestamp.now(),
        isDeleted: true
      });
      // Remove the question ID from all lessons that contain it
      await this.removeQuestionFromAllLessons(tenantId, [question.id]);
    } catch (error) {
      console.error("Error deleting question:", error);
      throw new Error("Failed to delete question. Please try again later.");
    }
  }

  async deleteMultipleQuestions(
    tenantId: string,
    questions: AssessmentQuestion[]
  ) {
    try {
      const questionRefs = questions.map((question) =>
        doc(
          this.firestore,
          `schools/${tenantId}/assessmentQuestions/${question.id}`
        )
      );

      const batch = writeBatch(this.firestore);
      questionRefs.forEach((questionRef) => {
        batch.update(questionRef, {
          deletedAt: Timestamp.now(),
          isDeleted: true
        });
      });
      await batch.commit();

      // Remove question IDs from all lessons that contain them
      const questionIds = questions.map(question => question.id);
      await this.removeQuestionFromAllLessons(tenantId, questionIds);
    } catch (error) {
      console.error("Error deleting questions:", error);
      throw new Error("Failed to delete questions. Please try again later.");
    }
  }

  async updateQuestion(tenantId: string, question: AssessmentQuestion) {
    try {
      const questionRef = doc(
        this.firestore,
        `schools/${tenantId}/assessmentQuestions/${question.id}`
      );

      const questionDoc = await getDoc(questionRef);
      if (!questionDoc.exists()) {
        throw new Error("Question not found");
      }
      const questionData = questionDoc.data() as AssessmentQuestion;
      if (question?.relatedLessonId && question.relatedLessonId !== '') {
        questionData.relatedLessonId = question.relatedLessonId;
      }
      await updateDoc(questionRef, {
        ...questionData,
        ...question,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error("Error updating question:", error);
      throw new Error("Failed to update question. Please try again later.");
    }
  }

  async updateMultipleQuestions(
    tenantId: string,
    questions: AssessmentQuestion[],
    tags: AssessmentQuestionTag[]
  ): Promise<void> {
    try {
      const batch = writeBatch(this.firestore);

      // Get existing tags for each question and merge with new tags
      for (const question of questions) {
        const questionRef = doc(
          this.firestore,
          `schools/${tenantId}/assessmentQuestions/${question.id}`
        );

        // Get current tags from the question object
        const existingTags = question.tags || [];
        const newTagIds = tags.map((tag) => tag.id);

        // Merge existing and new tags, removing duplicates
        const mergedTags = [...new Set([...existingTags, ...newTagIds])];

        batch.update(questionRef, { tags: mergedTags });
      }

      await batch.commit();
    } catch (error) {
      console.error("Error updating questions:", error);
      throw new Error("Failed to update questions. Please try again later.");
    }
  }


  async addQuestionsSelectionCriteriaToLesson(
    tenantId: string,
    questions: string[],
    courseId: string,
    lessonId: string,
    ruleTags: string[],
    ruleQuestionTypes: QuestionTypeFilter[],
    limit: number,
  ) {
    try {

      const lessonRef = doc(
        this.firestore,
        `schools/${tenantId}/courses/${courseId}/lessons/${lessonId}`
      );
      const lessonDoc = await getDoc(lessonRef);
      if (!lessonDoc.exists()) {
        throw new Error("Lesson not found");
      }
      const lessonData = lessonDoc.data() as Lesson;
      const assessment = lessonData.assessment as Assessment;
        const numberOfQuestions = questions.length;
        if (numberOfQuestions === 0) {
          this.messages.info('Questions with that criteria already exist in the assessment');
          return;
        }
      
        const newRule = {
          id: createFirestoreId(this.firestore),
          totalQuestions: numberOfQuestions,
          tags: ruleTags || [],
          questionType: ruleQuestionTypes || [],
          limit:limit
        };
      
        const existingRules = assessment.selectionRules || [];
      
        const isSubset = (subset, superset) =>
          subset.every(v => superset.includes(v));
      
        const isDuplicate = existingRules.some(rule =>
          isSubset(newRule.tags, rule.tags) &&
          isSubset(
            newRule.questionType.map(qt => qt.type),
            (rule.questionType || []).map(qt => qt.type)
          )
        );
      
        if (isDuplicate) {
          this.messages.info('Rule combination already exists');
          return;
        }
      
        const updatedRules = [...existingRules, newRule];
      
        await updateDoc(lessonRef, {
          assessment: {
            ...assessment,
            selectionRules: updatedRules,
          },
        });
      
    }
    catch(error){
      console.error("Error adding questions selection criteria to lesson", error);
      throw new Error(
        "Failed to add questions selection criteria. Please try again later."
      );
    }
  }

  async addQuestionsToLesson(
    tenantId: string,
    questions: string[],
    courseId: string,
    lessonId: string,
    copyQuestionId: string = '',
  ) {
    try {
      const lessonRef = doc(
        this.firestore,
        `schools/${tenantId}/courses/${courseId}/lessons/${lessonId}`
      );
      const lessonDoc = await getDoc(lessonRef);
      if (!lessonDoc.exists()) {
        throw new Error("Lesson not found");
      }
      const lessonData = lessonDoc.data() as Lesson;
      const assessment = lessonData.assessment as Assessment;

      let existingQuestionIds: string[] =
      lessonData?.assessment?.assessmentQuestionIds || [];

    for (const questionId of questions) {
      if (!existingQuestionIds.includes(questionId)) {
        if (copyQuestionId) {
          existingQuestionIds.splice(
            existingQuestionIds.indexOf(copyQuestionId),
            0,
            questionId
          );
        } else {
          existingQuestionIds.push(questionId);
        }
      }
    }
      await updateDoc(lessonRef, {        
        assessment: {
          ...lessonData?.assessment,
          assessmentQuestionIds: existingQuestionIds,
        },
      });
    
    } catch (error) {
      console.error("Error adding questions to lesson:", error);
      throw new Error(
        "Failed to add questions to lesson. Please try again later."
      );
    }
  }

  async getLesson(tenantId: string, courseId: string, lessonId: string) {
    const lessonRef = doc(
      this.firestore,
      `schools/${tenantId}/courses/${courseId}/lessons/${lessonId}`
    );
    const lessonDoc = await getDoc(lessonRef);
    return lessonDoc.data() as Lesson;
  }

  async updateAssessment(
    tenantId: string,
    courseId: string,
    lessonId: string,
    assessment: Assessment
  ) {
    const lessonRef = doc(
      this.firestore,
      `schools/${tenantId}/courses/${courseId}/lessons/${lessonId}`
    );
    await updateDoc(lessonRef, { assessment });
  }

  private generateId(): string {
    return createFirestoreId(this.firestore);
  }



  private async removeQuestionFromAllLessons(
    tenantId: string,
    questionIds: string[]
  ): Promise<void> {
    try {
      const coursesSnapshot = await getDocs(
        collection(this.firestore, `schools/${tenantId}/courses`)
      );

      const updatePromises: Promise<any>[] = [];

      for (const courseDoc of coursesSnapshot.docs) {
        const courseId = courseDoc.id;
        const lessonsSnapshot = await getDocs(
          collection(this.firestore, `schools/${tenantId}/courses/${courseId}/lessons`)
        );

        for (const lessonDoc of lessonsSnapshot.docs) {
          const lessonData = lessonDoc.data() as Lesson;
          const assessmentQuestionIds =
            lessonData?.assessment?.assessmentQuestionIds || [];

          const updatedQuestionIds = assessmentQuestionIds.filter(
            (id) => !questionIds.includes(id)
          );

          // Only update if something actually changed
          if (updatedQuestionIds.length !== assessmentQuestionIds.length) {
            const lessonRef = lessonDoc.ref;
            updatePromises.push(
              updateDoc(lessonRef, {
                assessment: {
                  ...lessonData.assessment,
                  assessmentQuestionIds: updatedQuestionIds,
                },
              })
            );
          }
        }
      }

      // Run all updates in parallel
      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error removing question from all lessons:", error);
    }
  }
  async getRandomQuestions(tenantId: string, tags: string[], questionTypes: string[], limit: number) {
    try {

      const response = await  firstValueFrom(this.httpClient.get(environment.api.getRandomQuestionBankQuestions, {
        params: {
          tenantId: tenantId,
          tags: tags?.join(','),
          questionTypes: questionTypes?.join(','),
          limit: limit.toString(),
        }
      }));
      console.log('Random Questions', response);
      return response;
    } catch (error) {
      console.error("Error getting random questions:", error);
      throw new Error("Failed to get random questions. Please try again later.");
    }
  }

  async getRandomQuestionsList(input: Array<{ tenantId: string; tags: string[]; questionTypes: string[]; limit: number }>): Promise<any> {
    try {
      const response = await  firstValueFrom(this.httpClient.post(environment.api.getRandomQuestionBankQuestions, {list:input}));
      console.log('Random Questions', response);
      return response;
    } catch (error) {
      console.error("Error getting random questions:", error);
      throw new Error("Failed to get random questions. Please try again later.");
    }
  }
}
